import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconBuildingHospital,
  IconClipboardList,
  IconLayoutDashboard,
  IconServer,
  IconUsers,
} from '@tabler/icons-react';
import axios from '../../library/axios';
import { fetchCpanelSystemAvailability, redirectToCpanelSystem } from '../../helpers/cpanelAuth';
import { MySwal } from '../../library/swal';
import { logo } from '../../assets';
import { APP_VERSION } from '../../constants/appVersion';
import { getApiErrorMessage } from '../../helpers/apiErrorMessage';

const buildMenuSections = (showCpanelSystem) => {
  const sections = [
    {
      title: 'General',
      items: [
        { label: 'Dashboard', path: '/cpanel/dashboard', exact: true, Icon: IconLayoutDashboard },
      ],
    },
    {
      title: 'Hospital',
      items: [
        { label: 'Tenant', path: '/cpanel/tenants', Icon: IconBuildingHospital },
        { label: 'User', path: '/cpanel/users', Icon: IconUsers },
      ],
    },
  ];

  if (showCpanelSystem) {
    sections.push({
      title: 'System',
      items: [
        { label: 'CPanel System', action: 'cpanel-system', Icon: IconServer },
      ],
    });
  }

  sections.push({
    title: 'Activity',
    items: [
      { label: 'Log', path: '/cpanel/logs', Icon: IconClipboardList },
    ],
  });

  return sections;
};

const CpanelNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openingSystem, setOpeningSystem] = useState(false);
  const [cpanelSystemAvailable, setCpanelSystemAvailable] = useState(false);
  const menuSections = useMemo(
    () => buildMenuSections(cpanelSystemAvailable),
    [cpanelSystemAvailable],
  );

  useEffect(() => {
    let active = true;

    fetchCpanelSystemAvailability(axios).then((available) => {
      if (active) setCpanelSystemAvailable(available);
    });

    return () => {
      active = false;
    };
  }, []);

  const isActive = (path, exact) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);
  const handleNav = (path) => (e) => {
    e.preventDefault();
    navigate(path);
  };

  const handleOpenCpanelSystem = async (e) => {
    e.preventDefault();
    if (openingSystem) return;
    setOpeningSystem(true);
    try {
      await redirectToCpanelSystem(axios);
    } catch (error) {
      setOpeningSystem(false);
      MySwal.fire({
        icon: 'error',
        title: 'Gagal membuka CPanel System',
        text: getApiErrorMessage(error, 'Terjadi kesalahan'),
      });
    }
  };

  return (
    <div
      id="kt_aside"
      className="aside cpanel-aside"
      data-kt-drawer="true"
      data-kt-drawer-name="aside"
      data-kt-drawer-activate='{"default": true, "lg": false}'
      data-kt-drawer-overlay="true"
      data-kt-drawer-width='{"default":"200px","300px":"250px"}'
      data-kt-drawer-direction="start"
      data-kt-drawer-toggle="#kt_aside_mobile_toggle"
    >
      <div className="aside-logo flex-column-auto aside-logo-custom cpanel-aside-logo" id="kt_aside_logo">
        <a href="/cpanel/dashboard" onClick={handleNav('/cpanel/dashboard')} className="cpanel-aside-logo__link">
          <img alt="LAM-KPRS" src={logo} className="cpanel-aside-logo__img" />
        </a>
      </div>
      <div className="aside-menu flex-column-fluid">
        <div
          className="hover-scroll-overlay-y cpanel-aside-menu-scroll"
          id="kt_aside_menu_wrapper"
          data-kt-scroll="true"
          data-kt-scroll-activate="{default: false, lg: true}"
          data-kt-scroll-height="auto"
          data-kt-scroll-dependencies="#kt_aside_logo, #kt_aside_footer"
          data-kt-scroll-wrappers="#kt_aside_menu"
          data-kt-scroll-offset={0}
        >
          <div
            className="menu menu-column menu-state-title-primary menu-state-icon-primary menu-state-bullet-primary"
            id="kt_aside_menu"
            data-kt-menu="true"
          >
            {menuSections.map((section) => (
              <React.Fragment key={section.title}>
                <div className="menu-item cpanel-aside-section-item">
                  <div className="menu-content pb-2">
                    <span className="menu-section cpanel-aside-section text-uppercase fs-8 ls-1">
                      {section.title}
                    </span>
                  </div>
                </div>
                {section.items.map((item) => {
                  const { Icon } = item;
                  if (item.action === 'cpanel-system') {
                    return (
                      <div className="menu-item" key={item.label}>
                        <a
                          href="/cpanel-system"
                          className="menu-link cpanel-aside-link"
                          onClick={handleOpenCpanelSystem}
                          title={item.label}
                          aria-disabled={openingSystem}
                        >
                          <span className="menu-icon">
                            <Icon size={22} stroke={1.75} />
                          </span>
                          <span className="menu-title">
                            {openingSystem ? 'Membuka...' : item.label}
                          </span>
                        </a>
                      </div>
                    );
                  }
                  return (
                    <div className="menu-item" key={item.path}>
                      <a
                        href={item.path}
                        className={`menu-link cpanel-aside-link${isActive(item.path, item.exact) ? ' active' : ''}`}
                        onClick={handleNav(item.path)}
                        title={item.label}
                      >
                        <span className="menu-icon">
                          <Icon size={22} stroke={1.75} />
                        </span>
                        <span className="menu-title">{item.label}</span>
                      </a>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      <div className="aside-footer flex-column-auto pt-2 pb-7 px-5 cpanel-aside-footer" id="kt_aside_footer">
        <a
          href="https://lam-kprs.id/"
          className="btn w-100 cpanel-aside-footer__btn btn-custom"
          target="_blank"
          rel="noopener noreferrer"
          title={`LAM-KPRS v${APP_VERSION}`}
        >
          <span className="btn-label">
            {new Date().getFullYear()}© | LAM-KPRS
            <span className="cpanel-aside-footer__version"> · v{APP_VERSION}</span>
          </span>
          <span className="btn-icon cpanel-aside-footer__icon" aria-hidden="true">©</span>
        </a>
      </div>
    </div>
  );
};

export default CpanelNavbar;
